import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog.tsx";
import useModelCode from "@/features/sub-families/hooks/use-model-code.ts";
import useTracking from "@/features/tracking/hooks/use-tracking.ts";
import { useTranslations } from "@/features/translations/components/translations";
import { useAppSelector } from "@/redux/store.ts";

type Props = { crmPath: string };

type SetHeightEvent = { type: "setHeight"; height: number };
type SubmitEvent = { type: "submit" };
type FormLoadedEvent = { type: "formLoaded" };
type MessageEvents = SetHeightEvent | SubmitEvent | FormLoadedEvent;

export default function ContactForm({ crmPath }: Props) {
    const t = useTranslations();

    const trackEvent = useTracking();
    const modelCode = useModelCode();

    const ref = useRef<HTMLIFrameElement>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);

    const smartNumber = useAppSelector((state) => state.smartNumber.calculatedResult);
    const family = useAppSelector((state) => state.subFamily.family);
    const subFamily = useAppSelector((state) => state.subFamily.name);

    const [open, setOpen] = useState<boolean>(false);

    useEffect(() => {
        const abortController = new AbortController();

        window.addEventListener(
            "message",
            (event: MessageEvent<MessageEvents>) => {
                if (event.data === null || typeof event.data !== "object") return;

                switch (event.data.type) {
                    case "setHeight":
                        dialogRef.current?.style.setProperty("height", `${event.data.height}px`);
                        break;

                    case "submit":
                        trackEvent("3d-contactform", {
                            action: "submit",
                            component: "summary",
                            model_code: modelCode ?? "",
                            event: "contact_form",
                        });
                        break;

                    case "formLoaded":
                        ref.current?.contentWindow?.postMessage(
                            {
                                type: "ci-prefill",
                                variables: {
                                    "HF - Product - Configuratorcode": smartNumber,
                                    "HF - Product - Family": family,
                                    "HF - Product - Subfamily": subFamily,
                                },
                            },
                            new URL(crmPath).origin,
                        );
                        break;

                    default:
                        break;
                }
            },
            { signal: abortController.signal },
        );

        return () => {
            abortController.abort();
        };
    }, [crmPath, family, modelCode, smartNumber, subFamily, trackEvent]);

    if (!smartNumber) return null;

    const trackOpenForm = () => {
        trackEvent("3d-contactform", { action: "open", component: "summary", model_code: modelCode ?? "" });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger variant="default" background="dark" className="contact-me__button" onClick={trackOpenForm}>
                {t("contact_me")}
            </DialogTrigger>
            <DialogContent
                ref={dialogRef}
                className="dialog--contact-me"
                id="contact-me-dialog"
                closeOnClickOutside={false}
            >
                <DialogHeader>
                    <DialogTitle>{t("contact_me")}</DialogTitle>
                </DialogHeader>
                {/* Only render the form when we open the dialog. This fixes issues with invalid tokens and the like */}
                {open && <iframe ref={ref} className="contact-me__form" title="contact form" src={crmPath} />}
            </DialogContent>
        </Dialog>
    );
}
